import Resolver from "./resolver";
import Logger from "./logger";
import Ajax from "./ajax";
import RootResource from "./resources/rootResource";
import Environment from "../environment";

// The Octopus Client implements the low-level semantics of the Octopus Deploy REST API
class Client {
    version: string;
    resolve: (path: string, uriTemplateParameters?: any) => string;
    private currentResolver: Resolver;
    private rootDocument: RootResource;
    constructor(configuration: any) {

        configuration = this.extend({serverEndpoint: null}, configuration);
        const endpoint = this.normalizeEndpoint(configuration.serverEndpoint);

        Logger.log("Creating Octopus client for endpoint: " + endpoint);

        this.currentResolver = new Resolver(endpoint);
        this.rootDocument = null;
        this.resolve = this.currentResolver.resolve.bind(this.currentResolver);
    }
    connect(progressCallback: any) {
        progressCallback("Checking your credentials. Please wait...", "");

        return new Promise((resolve, reject) => {
            if (this.rootDocument) {
                resolve(this.rootDocument);
                return;
            }

            const attempt = (success: any, fail: any) => {
                this.get("~/api")
                    .then(success, fail);
            };

            const onSuccess = (root: any) => {
                this.rootDocument = root;
                this.version = root.Version;
                resolve(this.rootDocument);
            };

            let fails = 0;
            const onFail = (err: any) => {
                if (err.StatusCode !== 503) {
                    fails++;
                }

                if ((err.StatusCode === 0 || err.StatusCode === 503) && fails < 20) {
                    Logger.warn("Unable to connect, trying again in 1 second...", err);

                    if (err.StatusCode === 503) {
                        progressCallback(err.ErrorMessage, "success");
                    } else if (err.StatusCode === 0) {
                        progressCallback("The Octopus Server does not appear to have started, trying again...",
                            "warning");
                    }

                    setTimeout(() => {
                        Logger.warn("Trying again", err);
                        attempt(onSuccess, onFail);
                    }, 1000);
                } else {
                    progressCallback("Unable to connect, unexpected error, or timeout exceeded.", "error");
                    Logger.error("Unable to connect, unexpected error, or timeout exceeded", err);
                    reject(err);
                }
            };

            attempt(onSuccess, onFail);
        });
    }
    get<TResource>(path: string, args?: any): Promise<TResource> {
        const url = this.currentResolver.resolve(path, args);
        return this.dispatchRequest("GET", url) as Promise<TResource>;
    }
    getRaw(path: string, args?: any): Promise<string> {
        const url = this.currentResolver.resolve(path, args);

        return new Promise((resolve, reject) => {
            new Ajax({
                client: this,
                method: "GET",
                error: e => reject(e),
                url,
                raw: true,
                success: data => resolve(data)
            }).execute();
        });
    }

    post<TReturn>(path: string, resource?: any, args?: any): Promise<TReturn> {
        const url = this.currentResolver.resolve(path, args);
        return this.dispatchRequest("POST", url, resource) as Promise<TReturn>;
    }

    create<TNewResource, TResource>(path: string, resource: TNewResource, args: any): Promise<TResource> {
        const url = this.currentResolver.resolve(path, args);

        return new Promise((resolve, reject) => {
            this.dispatchRequest("POST", url, resource)
                .then((result: any) => {
                    this.dispatchRequest("GET", this.currentResolver.resolve(result.Links.Self))
                        .then((result2: TResource) => {
                            resolve(result2);
                        }, reject);
                }, reject);
        });
    }

    update<TResource>(path: string, resource: TResource, args?: any): Promise<TResource> {
        const url = this.currentResolver.resolve(path, args);

        return new Promise((resolve, reject) => {
            this.dispatchRequest("PUT", url, resource)
                .then(() => {
                    this.dispatchRequest("GET", url)
                        .then((result2: TResource) => {
                            resolve(result2);
                        }, reject);
                }, reject);
        });
    }

    del(path: string, resource?: any, args?: any) {
        const url = this.currentResolver.resolve(path, args);
        return this.dispatchRequest("DELETE", url, resource);
    }

    put<TResource>(path: string, resource?: TResource): Promise<TResource> {
        const url = this.currentResolver.resolve(path);
        return this.dispatchRequest("PUT", url, resource) as Promise<TResource>;
    }

    getAntiforgeryToken() {
        if (!this.isConnected()) {
            return null;
        }

        const installationId = this.getRootDocument().InstallationId;
        if (!installationId) {
            return null;
        }

        // If we have come this far we know we are on a version of Octopus Server which supports anti-forgery tokens
        const antiforgeryCookieName = "Octopus-Csrf-Token_" + installationId;
        const antiforgeryCookies = document.cookie.split(";").filter((c) => {
            return c.trim().indexOf(antiforgeryCookieName) === 0;
        }).map((c) => {
            return c.trim();
        });

        if (antiforgeryCookies && antiforgeryCookies.length === 1) {
            const antiforgeryToken = antiforgeryCookies[0].split("=")[1];
            return antiforgeryToken;
        } else {
            if (Environment.isInDevelopmentMode()) {
                return "FAKE TOKEN USED FOR DEVELOPMENT";
            }
            return null;
        }
    }

    getRootDocument() {
        if (!this.isConnected()) {
            throw new Error("The Octopus Client has not connected. THIS SHOULD NOT HAPPEN! Please notify support.");
        }
        return this.rootDocument;
    }

    resolveLinkTemplate(link: string, args: any) {
        return this.resolve(this.getRootDocument().Links[link], args);
    }

    private extend(arg1: any, arg2: any) {
        return {...arg1, ...arg2};
    }
    private dispatchRequest(method: any, url: string, requestBody?: any) {
        return new Promise((resolve, reject) => {
            new Ajax({
                client: this,
                error: e => reject(e),
                method,
                url,
                requestBody,
                success: data => resolve(data),
            }).execute();
        });
    }

    private isConnected() {
        return this.rootDocument !== null;
    }
    private normalizeEndpoint(endpoint: any) {
        if (endpoint == null || endpoint.length === 0) {
            endpoint = "" + window.location.protocol;
            if (!endpoint.endsWith("//")) {
                endpoint = endpoint + "//";
            }

            endpoint = endpoint + window.location.host;

            let path = window.location.pathname;
            if (!path.startsWith("/")) {
                path = "/" + path;
            }

            if (path.length >= 1) {
                const lastSegmentIndex = path.lastIndexOf("/");
                if (lastSegmentIndex >= 0) {
                    path = path.substring(0, lastSegmentIndex + 1);
                }
            }

            endpoint = endpoint + path;
        }
        return endpoint;
    }
}

export default Client;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/client/client.ts